import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api.js';
import { num, pct } from '../lib/format.js';
import { EmptyState, ErrorState, LoadingState, PageHeader, Panel, Tile } from '../components/ui.js';
import '../screens-2a.css';

interface RevenueData {
  flexiByLeadBand: Array<{ band: string; trips: number; with_flexi: number }>;
  guideConversion: { detected: number; converted: number };
  upgradeAfterM04: { sent: number; converted: number };
  nextSeasonAfterM05: Array<{ holdout: boolean; exposed: number; converted: number }>;
  holdoutCompare: Array<{ holdout: boolean; contacts: number; buyers: number }>;
  gifts: { total: number; redeemed: number; expired: number };
}

const share = (part: number, whole: number): number | null =>
  whole > 0 ? Math.round((part / whole) * 100) : null;

export function Revenue(): JSX.Element {
  const query = useQuery<RevenueData>({
    queryKey: ['revenue'],
    queryFn: () => api.get('/api/revenue'),
  });

  if (query.isLoading) return <LoadingState what="výnosy" />;
  if (query.isError)
    return (
      <ErrorState
        title="Výnosy se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return <LoadingState />;

  const exposed = data.nextSeasonAfterM05.find((r) => !r.holdout);
  const control = data.nextSeasonAfterM05.find((r) => r.holdout);
  const exposedRate = exposed ? share(exposed.converted, exposed.exposed) : null;
  const controlRate = control ? share(control.converted, control.exposed) : null;

  return (
    <>
      <PageHeader
        title="Výnosy"
        description="Kolik nákupů komunikace přinesla a jak si proti ní stojí kontrolní skupina, tedy zákazníci, kterým marketing záměrně nechodí. Bez kontrolní skupiny nejde poznat, co je zásluha komunikace."
      />

      <div className="tiles">
        <Tile
          label="„Jen průvodce“ → vstupenka"
          value={pct(share(data.guideConversion.converted, data.guideConversion.detected))}
          note={`${num(data.guideConversion.converted)} z ${num(data.guideConversion.detected)}`}
        />
        <Tile
          label="Upgrade do 14 dní po M04"
          value={pct(share(data.upgradeAfterM04.converted, data.upgradeAfterM04.sent))}
          note={`${num(data.upgradeAfterM04.converted)} z ${num(data.upgradeAfterM04.sent)}`}
        />
        <Tile
          label="Další ročník do 30 dní po M05"
          value={pct(exposedRate)}
          note={exposed ? `${num(exposed.converted)} z ${num(exposed.exposed)}` : 'zatím bez dat'}
        />
        <Tile
          label="Totéž u kontrolní skupiny"
          value={pct(controlRate)}
          note={control ? `${num(control.converted)} z ${num(control.exposed)}` : 'zatím bez dat'}
        />
        <Tile
          label="Rozdíl proti kontrolní skupině"
          value={
            exposedRate !== null && controlRate !== null
              ? `${exposedRate - controlRate > 0 ? '+' : ''}${exposedRate - controlRate} b. p.`
              : '—'
          }
          note="procentní body"
        />
        <Tile
          label="Uplatnění dárků"
          value={pct(share(data.gifts.redeemed, data.gifts.total))}
          note={`${num(data.gifts.redeemed)} z ${num(data.gifts.total)}, ${num(data.gifts.expired)} propadlo`}
        />
      </div>

      <Panel title="Podíl cest s Flexi podle pásma předstihu">
        {data.flexiByLeadBand.length === 0 ? (
          <EmptyState title="Zatím není z čeho počítat">
            Podíl se spočítá, jakmile budou v hubu cesty s vyplněným předstihem objednávky.
          </EmptyState>
        ) : (
          <>
            <div className="s2a-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.flexiByLeadBand.map((r) => ({
                    band: r.band,
                    podil: share(r.with_flexi, r.trips) ?? 0,
                  }))}
                >
                  <CartesianGrid stroke="var(--grid)" vertical={false} />
                  <XAxis dataKey="band" stroke="var(--muted)" />
                  <YAxis stroke="var(--muted)" unit=" %" />
                  <Tooltip formatter={(v: number) => [`${v} %`, 'cest s Flexi']} />
                  <Bar dataKey="podil" fill="var(--chart1)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Pásmo předstihu</th>
                    <th className="n">Cest</th>
                    <th className="n">S Flexi</th>
                    <th className="n">Podíl</th>
                  </tr>
                </thead>
                <tbody>
                  {data.flexiByLeadBand.map((r) => (
                    <tr key={r.band}>
                      <td>{r.band}</td>
                      <td className="n">{num(r.trips)}</td>
                      <td className="n">{num(r.with_flexi)}</td>
                      <td className="n">{pct(share(r.with_flexi, r.trips))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      <Panel title="Kontrolní skupina">
        <p className="note">
          Kontrolní skupina je stabilní podíl kontaktů, kterým marketing nechodí. Rozdíl mezi
          skupinami je to jediné, co opravdu měří přínos komunikace.
        </p>
        {data.holdoutCompare.length === 0 ? (
          <EmptyState title="Zatím bez dat">
            Skupiny se porovnají, až hub eviduje první kontakty a nákupy.
          </EmptyState>
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Skupina</th>
                <th className="n">Kontaktů</th>
                <th className="n">Z toho nakoupilo</th>
                <th className="n">Podíl</th>
              </tr>
            </thead>
            <tbody>
              {data.holdoutCompare.map((r) => (
                <tr key={String(r.holdout)}>
                  <td>{r.holdout ? 'Kontrolní skupina' : 'Exponovaní'}</td>
                  <td className="n">{num(r.contacts)}</td>
                  <td className="n">{num(r.buyers)}</td>
                  <td className="n">{pct(share(r.buyers, r.contacts))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}
